import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode, RefObject } from 'react';
import { cn } from './cn';

/**
 * Generic side-panel modal — full-height, slides in from the right (or left).
 * Reused by Brand DNA (v1) and Profile (surface 6 — future). The primitive
 * is content-agnostic: it owns the chrome (slide-in shell, header, close
 * affordances, focus management) and the caller composes whatever sections
 * / forms / lists it needs inside.
 *
 * Esc / backdrop / header X all call `onOpenChange(false)`. Animation is
 * `slide-in-from-right` (or `-from-left`); removed under
 * `prefers-reduced-motion`.
 */
export interface SidePanelModalProps {
  /** Drives `<Dialog open>`. */
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Required — used as the dialog's accessible name. */
  ariaLabel: string;
  /** Optional sticky-header content (title row, status pill, etc.). */
  header?: ReactNode;
  children: ReactNode;
  /** Which edge slides in from. Defaults to `'right'`. */
  side?: 'right' | 'left';
  /** Body width on `≥sm`; full-width on mobile. Defaults to `'default'` (520 px). */
  width?: 'default' | 'wide';
  /**
   * Element to focus when the panel opens. When absent, Radix focuses the
   * first focusable element (the close button). Pass a ref to a form input
   * so keyboard users land in the right place immediately.
   */
  initialFocus?: RefObject<HTMLElement>;
  className?: string;
}

const WIDTH: Record<NonNullable<SidePanelModalProps['width']>, string> = {
  default: 'sm:w-[520px]',
  wide: 'sm:w-[680px]',
};

export function SidePanelModal({
  open,
  onOpenChange,
  ariaLabel,
  header,
  children,
  side = 'right',
  width = 'default',
  initialFocus,
  className,
}: SidePanelModalProps) {
  const slideInClass =
    side === 'right'
      ? 'right-0 data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right'
      : 'left-0 data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <Dialog.Content
          aria-describedby={undefined}
          aria-label={ariaLabel}
          onOpenAutoFocus={(e) => {
            if (initialFocus?.current) {
              e.preventDefault();
              initialFocus.current.focus();
            }
          }}
          className={cn(
            'fixed top-0 z-50 flex h-dvh w-full flex-col overflow-hidden border-l border-border bg-bg text-fg shadow-xl',
            slideInClass,
            'data-[state=open]:animate-in data-[state=closed]:animate-out motion-reduce:animate-none',
            WIDTH[width],
            className,
          )}
        >
          <Dialog.Title className="sr-only">{ariaLabel}</Dialog.Title>
          {header ? (
            <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border-subtle bg-bg/95 px-5 py-4 backdrop-blur">
              <div className="min-w-0 flex-1">{header}</div>
              <Dialog.Close
                aria-label="Close panel"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-fg-3 transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <X className="h-4 w-4" aria-hidden />
              </Dialog.Close>
            </div>
          ) : (
            <Dialog.Close
              aria-label="Close panel"
              className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-3 transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <X className="h-4 w-4" aria-hidden />
            </Dialog.Close>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
